import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import BottomNav from '../components/BottomNav';
import DeliveryCostCalculator from '../components/DeliveryCostCalculator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, MapPin, Truck, Store, CreditCard, Banknote, 
  Smartphone, Check, Loader2, Navigation, Package, Clock,
  ChevronDown, ChevronUp, Droplets, ShoppingBag, Gift
} from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { cart, fetchCart } = useCart();
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [nearestStation, setNearestStation] = useState(null);
  const [step, setStep] = useState(1); // 1: Delivery, 2: Payment, 3: Confirm
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [deliveryCost, setDeliveryCost] = useState(null);

  // Check if user is corporate client
  const isCorporate = user?.client_type === 'corporate';

  const [formData, setFormData] = useState({
    delivery_method: isCorporate ? 'delivery' : 'pickup', // Corporate must use delivery
    station_id: '',
    address: '',
    payment_method: 'multicaixa',
    notes: ''
  });

  const handleDeliveryCostCalculated = useCallback((cost) => {
    setDeliveryCost(cost);
  }, []);

  useEffect(() => {
    // If corporate client, force delivery method
    if (isCorporate) {
      setFormData(prev => ({ ...prev, delivery_method: 'delivery' }));
    }
  }, [isCorporate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchCart();
        const stationsRes = await api().get('/stations');
        setStations(stationsRes.data);
        // Auto-select first station
        if (stationsRes.data.length > 0) {
          setFormData(prev => ({ ...prev, station_id: stationsRes.data[0].id }));
        }
      } catch (err) {
        console.error('Error loading data:', err);
      }
      setLoading(false);
    };
    loadData();
  }, [api, fetchCart]);

  const findNearestStation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada pelo navegador');
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await api().get('/stations/nearest', {
            params: { lat: position.coords.latitude, lon: position.coords.longitude }
          });
          setNearestStation(res.data);
          setFormData(prev => ({ ...prev, station_id: res.data.station.id }));
          toast.success(`Posto mais próximo encontrado: ${res.data.station.name}`);
        } catch (err) {
          toast.error('Erro ao encontrar posto mais próximo');
        }
        setLocating(false);
      },
      (error) => {
        toast.error('Não foi possível obter a sua localização');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async () => {
    if (!formData.station_id) {
      toast.error('Seleccione uma estação de serviço');
      setStep(1);
      return;
    }
    if (formData.delivery_method === 'delivery' && !formData.address.trim()) {
      toast.error('Insira o endereço de entrega');
      setStep(1);
      return;
    }

    setSubmitting(true);
    try {
      const res = await api().post('/orders', formData);
      toast.success('Pedido criado com sucesso');
      navigate(`/order/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar pedido');
    }
    setSubmitting(false);
  };

  const paymentMethods = [
    { id: 'multicaixa', name: 'Multicaixa Express', icon: CreditCard, description: 'Pagamento digital' },
    { id: 'mobile_money', name: 'Mobile Money', icon: Smartphone, description: 'Unitel Money / Afrimoney' },
    { id: 'cash', name: 'Dinheiro', icon: Banknote, description: 'Pagar na entrega/recolha' },
  ];

  const selectedStation = stations.find(s => s.id === formData.station_id);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-sonangol-red border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-zinc-500">A carregar...</p>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-100 pb-40">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-zinc-200">
        <div className="px-4 py-3 flex items-center gap-3">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} 
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-barlow font-bold text-lg">Finalizar Compra</h1>
            <p className="text-xs text-zinc-500">Passo {step} de 3</p>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="px-4 pb-3 flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div 
              key={s} 
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                s <= step ? 'bg-sonangol-red' : 'bg-zinc-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Order Summary Toggle */}
      <div className="px-4 py-3 bg-white border-b border-zinc-200">
        <button 
          onClick={() => setShowOrderSummary(!showOrderSummary)}
          className="w-full flex items-center justify-between"
          data-testid="toggle-summary"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sonangol-red/10 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-sonangol-red" />
            </div>
            <div className="text-left">
              <p className="font-barlow font-bold text-zinc-800">
                {cart.items.length} {cart.items.length === 1 ? 'produto' : 'produtos'}
              </p>
              <p className="text-xs text-zinc-500">Toque para ver detalhes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-barlow font-bold text-sonangol-red text-lg">
              {cart.total_aoa.toLocaleString('pt-AO')} AOA
            </span>
            {showOrderSummary ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
          </div>
        </button>
        
        {showOrderSummary && (
          <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2">
            {cart.items.map((item) => (
              <div key={item.product.id} className="flex items-center gap-3">
                <div className="w-12 h-12 bg-zinc-100 rounded-lg flex items-center justify-center">
                  <Droplets className="w-6 h-6 text-sonangol-red" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-zinc-800 text-sm truncate">{item.product.name}</p>
                  <p className="text-xs text-zinc-500">{item.quantity}x {item.product.price_aoa.toLocaleString('pt-AO')} AOA</p>
                </div>
                <p className="font-barlow font-bold text-zinc-800">
                  {item.subtotal.toLocaleString('pt-AO')} AOA
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* STEP 1: Delivery Method & Station */}
        {step === 1 && (
          <>
            {/* Corporate Client Notice */}
            {isCorporate && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                <p className="text-orange-800 text-sm">
                  <strong>Cliente Corporate:</strong> As suas encomendas são entregues por transportadores especializados no endereço definido.
                </p>
              </div>
            )}

            {/* Delivery Method */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
              <h2 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-sonangol-red" />
                Como pretende receber?
              </h2>
              
              <div className="space-y-3">
                {/* Pickup option - disabled for corporate */}
                <div 
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    isCorporate 
                      ? 'border-zinc-200 bg-zinc-50 opacity-50 cursor-not-allowed'
                      : formData.delivery_method === 'pickup' 
                        ? 'border-sonangol-red bg-red-50 shadow-sm cursor-pointer' 
                        : 'border-zinc-200 hover:border-zinc-300 cursor-pointer'
                  }`}
                  onClick={() => !isCorporate && setFormData({ ...formData, delivery_method: 'pickup' })}
                  data-testid="delivery-pickup"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.delivery_method === 'pickup' && !isCorporate ? 'bg-sonangol-red' : 'bg-zinc-100'
                  }`}>
                    <Store className={`w-6 h-6 ${formData.delivery_method === 'pickup' && !isCorporate ? 'text-white' : 'text-zinc-500'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-barlow font-bold ${formData.delivery_method === 'pickup' && !isCorporate ? 'text-sonangol-red' : 'text-zinc-800'}`}>
                      Retirar na Bomba
                    </p>
                    <p className="text-xs text-zinc-500">
                      {isCorporate ? 'Indisponível para clientes corporate' : 'Levante o seu pedido na estação'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-green-600 font-bold text-sm">Grátis</span>
                  </div>
                </div>

                <div 
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.delivery_method === 'delivery' 
                      ? 'border-sonangol-orange bg-orange-50 shadow-sm' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                  onClick={() => setFormData({ ...formData, delivery_method: 'delivery' })}
                  data-testid="delivery-home"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.delivery_method === 'delivery' ? 'bg-sonangol-orange' : 'bg-zinc-100'
                  }`}>
                    <Truck className={`w-6 h-6 ${formData.delivery_method === 'delivery' ? 'text-white' : 'text-zinc-500'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-barlow font-bold ${formData.delivery_method === 'delivery' ? 'text-sonangol-orange' : 'text-zinc-800'}`}>
                      Entrega ao Domicílio
                    </p>
                    <p className="text-xs text-zinc-500">
                      {isCorporate ? 'Entrega por transportador corporate' : 'Receba na sua morada'}
                    </p>
                  </div>
                  <div className="text-right">
                    {deliveryCost && formData.delivery_method === 'delivery' ? (
                      deliveryCost.is_free ? (
                        <span className="text-green-600 font-bold text-sm">Grátis</span>
                      ) : (
                        <span className="text-sonangol-orange font-bold text-sm">{deliveryCost.delivery_cost.toLocaleString('pt-AO')} AOA</span>
                      )
                    ) : (
                      <span className="text-zinc-500 text-xs">A calcular</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Cost Calculator - Only show when delivery is selected */}
            {formData.delivery_method === 'delivery' && formData.station_id && (
              <DeliveryCostCalculator
                stationId={formData.station_id}
                cartTotal={cart.total_aoa}
                onCostCalculated={handleDeliveryCostCalculated}
              />
            )}

            {/* Station Selection */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-barlow font-bold text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-sonangol-red" />
                  {formData.delivery_method === 'pickup' ? 'Local de Recolha' : 'Bomba de Origem'}
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={findNearestStation}
                  disabled={locating}
                  className="text-xs h-11"
                  data-testid="find-nearest-btn"
                >
                  {locating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Navigation className="w-4 h-4 mr-1" />
                      Localizar
                    </>
                  )}
                </Button>
              </div>

              {nearestStation && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-green-800 font-medium text-sm">{nearestStation.station.name}</p>
                    <p className="text-green-600 text-xs">{nearestStation.distance_km} km de distância</p>
                  </div>
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stations.map((station) => (
                  <div
                    key={station.id}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.station_id === station.id 
                        ? 'border-sonangol-red bg-red-50 shadow-sm' 
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    onClick={() => setFormData({ ...formData, station_id: station.id })}
                    data-testid={`station-${station.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        formData.station_id === station.id ? 'bg-sonangol-red' : 'bg-zinc-100'
                      }`}>
                        <MapPin className={`w-5 h-5 ${formData.station_id === station.id ? 'text-white' : 'text-zinc-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-barlow font-semibold ${formData.station_id === station.id ? 'text-sonangol-red' : 'text-zinc-800'}`}>
                          {station.name}
                        </p>
                        <p className="text-xs text-zinc-500 truncate">{station.address}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3 text-zinc-400" />
                          <span className="text-xs text-zinc-400">{station.operating_hours}</span>
                        </div>
                      </div>
                      {formData.station_id === station.id && (
                        <Check className="w-5 h-5 text-sonangol-red flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Address (for delivery) */}
            {formData.delivery_method === 'delivery' && (
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
                <h2 className="font-barlow font-bold text-lg mb-4">Endereço de Entrega</h2>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Rua, número, bairro, município, ponto de referência..."
                  className="min-h-24 resize-none"
                  data-testid="address-input"
                />
                <p className="text-xs text-zinc-400 mt-2">
                  Inclua um ponto de referência para facilitar a entrega
                </p>
              </div>
            )}
          </>
        )}

        {/* STEP 2: Payment Method */}
        {step === 2 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
            <h2 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-sonangol-red" />
              Método de Pagamento
            </h2>
            
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === method.id 
                      ? 'border-sonangol-red bg-red-50 shadow-sm' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                  onClick={() => setFormData({ ...formData, payment_method: method.id })}
                  data-testid={`payment-${method.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.payment_method === method.id ? 'bg-sonangol-red' : 'bg-zinc-100'
                  }`}>
                    <method.icon className={`w-6 h-6 ${formData.payment_method === method.id ? 'text-white' : 'text-zinc-500'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-barlow font-bold ${formData.payment_method === method.id ? 'text-sonangol-red' : 'text-zinc-800'}`}>
                      {method.name}
                    </p>
                    <p className="text-xs text-zinc-500">{method.description}</p>
                  </div>
                  {formData.payment_method === method.id && (
                    <Check className="w-5 h-5 text-sonangol-red" />
                  )}
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="mt-6 pt-6 border-t border-zinc-100">
              <Label className="font-barlow font-semibold text-zinc-800">Observações (Opcional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Instruções especiais para o pedido..."
                className="mt-2 min-h-20 resize-none"
                data-testid="notes-input"
              />
            </div>
          </div>
        )}

        {/* STEP 3: Confirmation */}
        {step === 3 && (
          <>
            {/* Order Summary */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
              <h2 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-sonangol-red" />
                Resumo do Pedido
              </h2>
              
              <div className="space-y-3">
                {cart.items.map((item) => (
                  <div key={item.product.id} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0">
                    <div className="w-14 h-14 bg-zinc-100 rounded-lg flex items-center justify-center">
                      <Droplets className="w-7 h-7 text-sonangol-red" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-800 truncate">{item.product.name}</p>
                      <p className="text-sm text-zinc-500">{item.quantity}x {item.product.price_aoa.toLocaleString('pt-AO')} AOA</p>
                    </div>
                    <p className="font-barlow font-bold text-zinc-800">
                      {item.subtotal.toLocaleString('pt-AO')} AOA
                    </p>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-4 border-t-2 border-zinc-200 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-600">Subtotal dos Produtos</span>
                  <span className="font-semibold text-zinc-800">
                    {cart.total_aoa.toLocaleString('pt-AO')} AOA
                  </span>
                </div>
                
                {formData.delivery_method === 'delivery' && deliveryCost && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-600">Taxa de Entrega</span>
                    {deliveryCost.is_free ? (
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-400 line-through text-sm">{deliveryCost.base_cost.toLocaleString('pt-AO')} AOA</span>
                        <span className="font-semibold text-green-600 flex items-center gap-1">
                          <Gift className="w-4 h-4" />
                          Grátis
                        </span>
                      </div>
                    ) : (
                      <span className="font-semibold text-zinc-800">
                        {deliveryCost.delivery_cost.toLocaleString('pt-AO')} AOA
                      </span>
                    )}
                  </div>
                )}
                
                {formData.delivery_method === 'pickup' && (
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-600">Taxa de Entrega</span>
                    <span className="font-semibold text-green-600">Grátis (Recolha)</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center pt-2 mt-2 border-t border-zinc-100">
                  <span className="font-barlow font-bold text-lg">Total a Pagar</span>
                  <span className="font-barlow font-bold text-2xl text-sonangol-red">
                    {(cart.total_aoa + (formData.delivery_method === 'delivery' && deliveryCost ? deliveryCost.delivery_cost : 0)).toLocaleString('pt-AO')} AOA
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery Details */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
              <h2 className="font-barlow font-bold text-lg mb-4">Detalhes da Entrega</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sonangol-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    {formData.delivery_method === 'pickup' ? (
                      <Store className="w-5 h-5 text-sonangol-red" />
                    ) : (
                      <Truck className="w-5 h-5 text-sonangol-red" />
                    )}
                  </div>
                  <div>
                    <p className="font-barlow font-semibold text-zinc-800">
                      {formData.delivery_method === 'pickup' ? 'Recolha na Bomba' : 'Entrega ao Domicílio'}
                    </p>
                    <p className="text-sm text-zinc-600">{selectedStation?.name}</p>
                    <p className="text-xs text-zinc-400">{selectedStation?.address}</p>
                  </div>
                </div>

                {formData.address && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-sonangol-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-sonangol-orange" />
                    </div>
                    <div>
                      <p className="font-barlow font-semibold text-zinc-800">Endereço de Entrega</p>
                      <p className="text-sm text-zinc-600">{formData.address}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {paymentMethods.find(p => p.id === formData.payment_method)?.icon && (
                      React.createElement(paymentMethods.find(p => p.id === formData.payment_method).icon, {
                        className: "w-5 h-5 text-green-600"
                      })
                    )}
                  </div>
                  <div>
                    <p className="font-barlow font-semibold text-zinc-800">
                      {paymentMethods.find(p => p.id === formData.payment_method)?.name}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {paymentMethods.find(p => p.id === formData.payment_method)?.description}
                    </p>
                  </div>
                </div>

                {formData.notes && (
                  <div className="bg-zinc-50 rounded-xl p-3">
                    <p className="text-xs text-zinc-500 mb-1">Observações:</p>
                    <p className="text-sm text-zinc-700">{formData.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Loyalty Points Info */}
            <div className="bg-gradient-to-r from-sonangol-red to-sonangol-orange rounded-2xl p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-barlow font-bold">+{Math.floor(cart.total_aoa / 1000)} pontos de fidelidade</p>
                  <p className="text-white/80 text-xs">Serão adicionados após conclusão do pedido</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Fixed Bottom Bar */}
      <div className="fixed above-nav left-0 right-0 bg-white border-t border-zinc-200 px-4 py-4 z-30">
        <div className="flex gap-3">
          {step > 1 && (
            <Button
              variant="outline"
              className="h-12 px-6"
              onClick={() => setStep(step - 1)}
              data-testid="prev-step-btn"
            >
              Voltar
            </Button>
          )}
          
          {step < 3 ? (
            <Button
              className="flex-1 h-12 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider"
              onClick={() => setStep(step + 1)}
              data-testid="next-step-btn"
            >
              Continuar
            </Button>
          ) : (
            <Button
              className="flex-1 h-12 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="confirm-order-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  A processar...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Confirmar Pedido
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default CheckoutPage;
